/**
 * Task API - Centralized task mutation helpers with optimistic updates.
 *
 * Uses the generic optimisticMutation helper for the update-sync-rollback lifecycle.
 * Creation instead reconciles its own temporary row against the latest cache so
 * overlapping add requests cannot overwrite each other or roll back sibling tasks.
 * Each function provides only the config specific to its operation:
 * - applyOptimistic: how to update tasks instantly
 * - apiCall: what API request to make
 * - onSuccess: how to merge the server response (optional)
 *
 * Usage:
 *   import { completeTask, addTask } from '../lib/taskApi';
 *   const result = await completeTask(projectId, taskId, currentTasks);
 *   if (result.success) { tasks = result.tasks; }
 *   // complete / won't-do / move / delete return the optimistic list immediately;
 *   // commit and undo errors are handled inside the undo manager.
 *
 * @see docs/FRONTEND_ARCHITECTURE.md for patterns and decision framework
 * @see lib/optimisticMutation.ts for the generic mutation helper
 */

import type { Task, TaskStatus, TaskPriority } from './types';
import { applyTaskOptimisticUpdate } from './taskOptimistic';
import {
  setProjectTasks,
  getCachedTasks,
  addTaskToCache,
  confirmTaskCreation,
  invalidateProjectTasks,
  moveTaskBetweenProjectCaches,
  removeTaskFromCache,
  updateTaskInCache,
} from './stores/projectTasksCache';
import { jsonHeaders, readApiErrorMessage } from './apiHelpers';
import { optimisticMutation } from './optimisticMutation';
import { undoableAction, undoableSoftDelete } from './undoManager';
import { recordRecentMove } from './stores/recentMove';
import { markTaskRecentlyAdded } from './stores/recentlyAddedTask';
import { toast } from 'svelte-sonner';
import { syncNowFromTaskChange } from './stores/now';
import { isTempTaskId, registerTaskCreation, resolveTaskCreation } from './pendingTaskCreations';

/** Fetch + throw on non-ok response, return parsed JSON */
async function fetchJson<T>(url: string, init: RequestInit): Promise<T> {
  const response = await fetch(url, init);
  if (!response.ok) throw new Error(await readApiErrorMessage(response));
  return response.json();
}

/** Fetch + throw on non-ok response, return void */
async function fetchVoid(url: string, init: RequestInit): Promise<void> {
  const response = await fetch(url, init);
  if (!response.ok) throw new Error(await readApiErrorMessage(response));
}

export interface TaskMutationResult {
  success: boolean;
  tasks: Task[];
  error?: string;
}

/** addTask's internal result: also carries the saved row for the pending-creation registry. */
interface TaskCreationResult extends TaskMutationResult {
  created?: Task;
}

/** Internal helper: wraps optimisticMutation for the common task pattern */
async function taskMutation<TApiResult = void>(
  projectId: string,
  currentTasks: Task[],
  config: {
    applyOptimistic: (tasks: Task[]) => Task[];
    apiCall: () => Promise<TApiResult>;
    onSuccess?: (tasks: Task[], result: TApiResult) => Task[];
  },
): Promise<TaskMutationResult> {
  const result = await optimisticMutation<Task[], TApiResult>({
    getState: () => [...currentTasks],
    applyOptimistic: config.applyOptimistic,
    syncState: (tasks) => setProjectTasks(projectId, tasks),
    apiCall: config.apiCall,
    onSuccess: config.onSuccess,
  });
  if (!result.success && result.error) {
    toast.error(result.error);
  }
  return { success: result.success, tasks: result.state, error: result.error };
}

const NOT_SAVED_ERROR = 'Task could not be saved';

/** True while `taskId` is an optimistic row whose create request we are tracking. */
function isAwaitingCreate(taskId: string): boolean {
  return isTempTaskId(taskId) && resolveTaskCreation(taskId) !== undefined;
}

/** Replace the temp row with its saved task (or drop it if the list already has the saved row). */
function swapTempForSaved(tasks: Task[], tempId: string, saved: Task): Task[] {
  const hasSaved = tasks.some((t) => t.id === saved.id);
  return tasks.flatMap((t) => (t.id !== tempId ? [t] : hasSaved ? [] : [saved]));
}

/**
 * The list a queued mutation should replay against. The caller's list is as old
 * as the create request, and setProjectTasks drops cache rows missing from what
 * it is given, so replaying it would wipe rows added meanwhile. Prefer the live
 * cache; keep the caller's list only when the cache knows neither row.
 */
function replayBase(
  projectId: string,
  currentTasks: Task[],
  tempId: string,
  saved: Task | null | undefined,
): Task[] {
  const cached = getCachedTasks(projectId);
  const knowsRow = cached.tasks.some((t) => t.id === tempId || t.id === saved?.id);
  return cached.fetched && knowsRow ? cached.tasks : currentTasks;
}

/**
 * Queue a mutation on an unsaved (temp-) task: wait for its create request, then
 * replay `run` against the real id. The temp id is never sent to the server. If the
 * create failed the mutation is dropped; addTask has already toasted that failure.
 */
async function whenSaved(
  projectId: string,
  tempId: string,
  currentTasks: Task[],
  run: (savedId: string, tasks: Task[]) => Promise<TaskMutationResult>,
): Promise<TaskMutationResult> {
  const saved = await resolveTaskCreation(tempId);
  if (!saved) {
    return {
      success: false,
      tasks: replayBase(projectId, currentTasks, tempId, null).filter((t) => t.id !== tempId),
      error: NOT_SAVED_ERROR,
    };
  }
  return run(
    saved.id,
    swapTempForSaved(replayBase(projectId, currentTasks, tempId, saved), tempId, saved),
  );
}

function taskPreview(title: string, max = 40): string {
  return title.length > max ? `${title.slice(0, max)}…` : title;
}

async function patchTask(
  projectId: string,
  taskId: string,
  payload: Record<string, unknown>,
): Promise<Task> {
  return fetchJson<Task>(`/projects/${projectId}/tasks/${taskId}`, {
    method: 'PATCH',
    headers: jsonHeaders(),
    body: JSON.stringify({ task: payload }),
  });
}

/** Immediate-commit undo toast. Returns the optimistic list; commit errors stay in the manager. */
function undoableTaskChange(opts: {
  nextTasks: Task[];
  message: string;
  applyOptimistic: () => void;
  commit: () => Promise<void>;
  undoOptimistic: () => void;
  undo: () => Promise<void>;
  commitErrorMessage?: string;
  undoErrorMessage?: string;
}): TaskMutationResult {
  undoableAction({
    message: opts.message,
    duration: 5000,
    onOptimisticApply: opts.applyOptimistic,
    onCommit: opts.commit,
    onOptimisticUndo: opts.undoOptimistic,
    onUndo: opts.undo,
    onCommitError: opts.undoOptimistic,
    onUndoError: opts.applyOptimistic,
    commitErrorMessage: opts.commitErrorMessage,
    undoErrorMessage: opts.undoErrorMessage,
  });
  return { success: true, tasks: opts.nextTasks };
}

interface StatusSnapshot {
  status: TaskStatus;
  completedAt: string | null;
  isActive: boolean;
}

function undoableStatusChange(opts: {
  projectId: string;
  taskId: string;
  nextTasks: Task[];
  message: string;
  previous: StatusSnapshot;
  commit: () => Promise<Task>;
}): TaskMutationResult {
  const applied = opts.nextTasks.find((t) => t.id === opts.taskId);
  const forward: StatusSnapshot | undefined = applied
    ? { status: applied.status, completedAt: applied.completedAt, isActive: applied.isActive }
    : undefined;

  // setProjectTasks keeps the newer updatedAt row, so status snapshots must also
  // go through updateTaskInCache. onUndoError reuses applyOptimistic.
  const applyOptimistic = () => {
    setProjectTasks(opts.projectId, opts.nextTasks);
    if (forward) updateTaskInCache(opts.projectId, opts.taskId, forward);
  };

  return undoableTaskChange({
    nextTasks: opts.nextTasks,
    message: opts.message,
    applyOptimistic,
    commit: async () => {
      const updated = await opts.commit();
      updateTaskInCache(opts.projectId, opts.taskId, updated);
    },
    undoOptimistic: () => updateTaskInCache(opts.projectId, opts.taskId, opts.previous),
    undo: async () => {
      const updated = await patchTask(opts.projectId, opts.taskId, {
        status: opts.previous.status,
        is_active: opts.previous.isActive,
      });
      updateTaskInCache(opts.projectId, opts.taskId, updated);
    },
    commitErrorMessage: 'Failed to update task',
    undoErrorMessage: 'Failed to undo',
  });
}

/**
 * Toggle task completion status (todo ↔ done).
 * `todo → done` shows an undo toast. Reopen (`done` / `wont_do` → `todo`) does not.
 */
export async function completeTask(
  projectId: string,
  taskId: string,
  currentTasks: Task[],
  pendingUpdates?: Partial<Task>,
): Promise<TaskMutationResult> {
  if (isAwaitingCreate(taskId)) {
    return whenSaved(projectId, taskId, currentTasks, (id, tasks) =>
      completeTask(projectId, id, tasks, pendingUpdates),
    );
  }
  const task = currentTasks.find((t) => t.id === taskId);
  if (!task) return { success: false, tasks: currentTasks, error: 'Task not found' };

  if (task.status !== 'todo') {
    const updates = { ...pendingUpdates, status: 'todo' as TaskStatus };
    return taskMutation<Task>(projectId, currentTasks, {
      applyOptimistic: (tasks) =>
        tasks.map((t) =>
          t.id === taskId
            ? {
                ...t,
                ...pendingUpdates,
                status: 'todo',
                completedAt: null,
              }
            : t,
        ),
      apiCall: () => patchTask(projectId, taskId, updates),
      onSuccess: (tasks, updated) => tasks.map((t) => (t.id === taskId ? updated : t)),
    }).then((result) => {
      if (result.success) syncNowFromTaskChange(taskId, updates);
      return result;
    });
  }

  const previous: StatusSnapshot = {
    status: task.status,
    completedAt: task.completedAt,
    isActive: task.isActive,
  };
  const nextTasks = currentTasks.map((t) =>
    t.id === taskId
      ? {
          ...t,
          ...pendingUpdates,
          status: 'done' as TaskStatus,
          completedAt: new Date().toISOString(),
          isActive: false,
        }
      : t,
  );

  const result = undoableStatusChange({
    projectId,
    taskId,
    nextTasks,
    message: `Completed "${taskPreview(task.title)}"`,
    previous,
    commit: () => patchTask(projectId, taskId, { ...pendingUpdates, status: 'done' }),
  });
  syncNowFromTaskChange(taskId, { ...pendingUpdates, status: 'done' });
  return result;
}

/**
 * Add a new task to a project
 */
export async function addTask(
  projectId: string,
  title: string,
  currentTasks: Task[],
  options?: {
    notes?: string | null;
    priority?: TaskPriority;
    status?: TaskStatus;
    isActive?: boolean;
    tagNames?: string[];
  },
): Promise<TaskMutationResult> {
  const tempId = `temp-${crypto.randomUUID()}`;
  const cached = getCachedTasks(projectId);
  // The caller supplies the full list for an uncached project. Never reseed a
  // fetched cache from a possibly stale prop/list during another creation.
  if (!cached.fetched) setProjectTasks(projectId, currentTasks);
  const tasks = getCachedTasks(projectId).tasks;
  const notes = options?.notes ?? null;
  const priority = options?.priority ?? 'none';
  const status: TaskStatus = options?.status ?? 'todo';
  const isCompleted = status === 'done' || status === 'wont_do';
  const isActive = isCompleted ? false : (options?.isActive ?? false);
  const payload: {
    title: string;
    notes: string | null;
    priority: TaskPriority;
    status?: TaskStatus;
    is_active?: boolean;
    tag_names?: string[];
  } = { title, notes, priority };
  if (options?.status) payload.status = status;
  if (options?.tagNames?.length) payload.tag_names = options.tagNames;
  if (options?.isActive !== undefined) payload.is_active = isActive;

  const tempTask: Task = {
    id: tempId,
    projectId,
    title,
    notes,
    status,
    priority,
    position: tasks.filter((t) => t.status === 'todo').length,
    isActive,
    ...(options?.tagNames?.length
      ? { tags: options.tagNames.map((name) => ({ id: name, name })) }
      : {}),
    completedAt: isCompleted ? new Date().toISOString() : null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  // Creation is row-scoped: overlapping requests must not restore captured
  // whole-list snapshots on success or failure (rapid Return entry permits this).
  markTaskRecentlyAdded(tempId);
  addTaskToCache(projectId, tempTask);
  const creation = (async (): Promise<TaskCreationResult> => {
    try {
      const created = await fetchJson<Task>(`/projects/${projectId}/tasks`, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({ task: payload }),
      });
      confirmTaskCreation(projectId, tempId, created);
      markTaskRecentlyAdded(created.id);
      return { success: true, tasks: getCachedTasks(projectId).tasks, created };
    } catch (e) {
      removeTaskFromCache(projectId, tempId);
      const error = e instanceof Error ? e.message : String(e);
      toast.error(error);
      return { success: false, tasks: getCachedTasks(projectId).tasks, error };
    }
  })();
  // Mutations on the temp row queue behind this (see whenSaved). Registered
  // after the cache is reconciled, so replays see the confirmed row.
  registerTaskCreation(
    tempId,
    creation.then((r) => r.created ?? null),
  );
  const { created: _created, ...result } = await creation;
  return result;
}

/** Marker so copies are easy to spot next to the original. */
export function duplicatedTaskTitle(title: string): string {
  return title.endsWith(' (Copied)') ? title : `${title} (Copied)`;
}

/**
 * Copy a task into the same project with the same notes and priority.
 * The new task is always `todo` so a completed original still yields a fresh item.
 */
export async function duplicateTask(
  projectId: string,
  taskId: string,
  currentTasks: Task[],
): Promise<TaskMutationResult> {
  const task = currentTasks.find((t) => t.id === taskId);
  if (!task) return { success: false, tasks: currentTasks, error: 'Task not found' };

  const result = await addTask(projectId, duplicatedTaskTitle(task.title), currentTasks, {
    notes: task.notes,
    priority: task.priority,
  });
  if (result.success) {
    toast.success(`Duplicated "${taskPreview(task.title)}"`);
  }
  return result;
}

/**
 * Delete a task (soft-delete with undo toast)
 *
 * Uses undoableSoftDelete: immediately removes the task from the UI,
 * sends a DELETE request (soft-delete), and shows a toast with an Undo button.
 * Clicking Undo restores the task via the undiscard endpoint.
 */
export async function deleteTask(
  projectId: string,
  taskId: string,
  currentTasks: Task[],
): Promise<TaskMutationResult> {
  if (isAwaitingCreate(taskId)) {
    return whenSaved(projectId, taskId, currentTasks, (id, tasks) =>
      deleteTask(projectId, id, tasks),
    );
  }
  const taskToDelete = currentTasks.find((t) => t.id === taskId);
  if (!taskToDelete) return { success: false, tasks: currentTasks, error: 'Task not found' };

  const tasksAfterDelete = currentTasks.filter((t) => t.id !== taskId);

  const preview =
    taskToDelete.title.length > 40 ? taskToDelete.title.slice(0, 40) + '…' : taskToDelete.title;

  undoableSoftDelete({
    message: `Deleted "${preview}"`,
    onOptimisticRemove: () => setProjectTasks(projectId, tasksAfterDelete),
    onOptimisticRestore: () => setProjectTasks(projectId, currentTasks),
    onDelete: () =>
      fetchVoid(`/projects/${projectId}/tasks/${taskId}`, {
        method: 'DELETE',
        headers: jsonHeaders(),
      }),
    onRestore: async () => {
      const restored = await fetchJson<Task>(`/projects/${projectId}/tasks/${taskId}/undiscard`, {
        method: 'POST',
        headers: jsonHeaders(),
      });
      // Re-insert restored task into cache with fresh server data
      setProjectTasks(projectId, [...tasksAfterDelete, restored]);
    },
  });

  syncNowFromTaskChange(taskId, { deleted: true });
  return { success: true, tasks: tasksAfterDelete };
}

/**
 * Update task with arbitrary partial fields (generic version)
 * Used by TaskDetailPopover and similar UI that can update multiple fields at once.
 */
export async function updateTask(
  projectId: string,
  taskId: string,
  updates: Partial<Task>,
  currentTasks: Task[],
): Promise<TaskMutationResult> {
  if (isAwaitingCreate(taskId)) {
    return whenSaved(projectId, taskId, currentTasks, (id, tasks) =>
      updateTask(projectId, id, updates, tasks),
    );
  }
  return taskMutation<Task>(projectId, currentTasks, {
    applyOptimistic: (tasks) =>
      tasks.map((t) => (t.id === taskId ? applyTaskOptimisticUpdate(t, updates) : t)),
    apiCall: () =>
      fetchJson<Task>(`/projects/${projectId}/tasks/${taskId}`, {
        method: 'PATCH',
        headers: jsonHeaders(),
        body: JSON.stringify({ task: updates }),
      }),
    onSuccess: (tasks, updated) => tasks.map((t) => (t.id === taskId ? updated : t)),
  }).then((result) => {
    if (result.success) syncNowFromTaskChange(taskId, updates);
    return result;
  });
}

/**
 * Update task title
 */
export async function updateTaskTitle(
  projectId: string,
  taskId: string,
  newTitle: string,
  currentTasks: Task[],
): Promise<TaskMutationResult> {
  if (isAwaitingCreate(taskId)) {
    return whenSaved(projectId, taskId, currentTasks, (id, tasks) =>
      updateTaskTitle(projectId, id, newTitle, tasks),
    );
  }
  return taskMutation<Task>(projectId, currentTasks, {
    applyOptimistic: (tasks) => tasks.map((t) => (t.id === taskId ? { ...t, title: newTitle } : t)),
    apiCall: () =>
      fetchJson<Task>(`/projects/${projectId}/tasks/${taskId}`, {
        method: 'PATCH',
        headers: jsonHeaders(),
        body: JSON.stringify({ task: { title: newTitle } }),
      }),
    onSuccess: (tasks, updated) => tasks.map((t) => (t.id === taskId ? updated : t)),
  }).then((result) => {
    if (result.success) syncNowFromTaskChange(taskId, { title: newTitle });
    return result;
  });
}

/**
 * Update task priority
 */
export async function updateTaskPriority(
  projectId: string,
  taskId: string,
  newPriority: TaskPriority,
  currentTasks: Task[],
): Promise<TaskMutationResult> {
  if (isAwaitingCreate(taskId)) {
    return whenSaved(projectId, taskId, currentTasks, (id, tasks) =>
      updateTaskPriority(projectId, id, newPriority, tasks),
    );
  }
  return taskMutation<Task>(projectId, currentTasks, {
    applyOptimistic: (tasks) =>
      tasks.map((t) => (t.id === taskId ? { ...t, priority: newPriority } : t)),
    apiCall: () =>
      fetchJson<Task>(`/projects/${projectId}/tasks/${taskId}`, {
        method: 'PATCH',
        headers: jsonHeaders(),
        body: JSON.stringify({ task: { priority: newPriority } }),
      }),
    onSuccess: (tasks, updated) => tasks.map((t) => (t.id === taskId ? updated : t)),
  }).then((result) => {
    if (result.success) syncNowFromTaskChange(taskId, { priority: newPriority });
    return result;
  });
}

/**
 * Mark task as "won't do"
 *
 * Optional pendingUpdates are merged into the same API call (e.g., unsaved
 * title/notes edits from TaskDetailPopover that should be persisted alongside
 * the status change).
 */
export async function markTaskWontDo(
  projectId: string,
  taskId: string,
  currentTasks: Task[],
  pendingUpdates?: Partial<Task>,
): Promise<TaskMutationResult> {
  if (isAwaitingCreate(taskId)) {
    return whenSaved(projectId, taskId, currentTasks, (id, tasks) =>
      markTaskWontDo(projectId, id, tasks, pendingUpdates),
    );
  }
  const task = currentTasks.find((t) => t.id === taskId);
  if (!task) return { success: false, tasks: currentTasks, error: 'Task not found' };

  const previous: StatusSnapshot = {
    status: task.status,
    completedAt: task.completedAt,
    isActive: task.isActive,
  };
  const nextTasks = currentTasks.map((t) =>
    t.id === taskId
      ? {
          ...t,
          ...pendingUpdates,
          status: 'wont_do' as TaskStatus,
          completedAt: new Date().toISOString(),
          isActive: false,
        }
      : t,
  );

  const result = undoableStatusChange({
    projectId,
    taskId,
    nextTasks,
    message: `Marked "${taskPreview(task.title)}" as won't do`,
    previous,
    commit: () => patchTask(projectId, taskId, { ...pendingUpdates, status: 'wont_do' }),
  });
  syncNowFromTaskChange(taskId, { ...pendingUpdates, status: 'wont_do' });
  return result;
}

/**
 * Move task to another project
 */
export async function moveTaskToProject(
  sourceProjectId: string,
  targetProjectId: string,
  taskId: string,
  currentTasks: Task[],
  options?: { targetProjectTitle?: string },
): Promise<TaskMutationResult> {
  if (isAwaitingCreate(taskId)) {
    return whenSaved(sourceProjectId, taskId, currentTasks, (id, tasks) =>
      moveTaskToProject(sourceProjectId, targetProjectId, id, tasks, options),
    );
  }
  const taskToMove = currentTasks.find((t) => t.id === taskId);
  if (!taskToMove) {
    invalidateProjectTasks(targetProjectId);
    return taskMutation(sourceProjectId, currentTasks, {
      applyOptimistic: (tasks) => tasks.filter((t) => t.id !== taskId),
      apiCall: () =>
        fetchJson<Task>(`/projects/${sourceProjectId}/tasks/${taskId}/move`, {
          method: 'POST',
          headers: jsonHeaders(),
          body: JSON.stringify({ target_project_id: targetProjectId }),
        }),
    });
  }

  const nextTasks = currentTasks.filter((t) => t.id !== taskId);
  const targetName = options?.targetProjectTitle
    ? taskPreview(options.targetProjectTitle, 30)
    : 'another project';

  return undoableTaskChange({
    nextTasks,
    message: `Moved "${taskPreview(taskToMove.title, 30)}" → ${targetName}`,
    applyOptimistic: () => {
      moveTaskBetweenProjectCaches(sourceProjectId, targetProjectId, taskToMove);
      setProjectTasks(sourceProjectId, nextTasks);
    },
    commit: async () => {
      const moved = await fetchJson<Task>(`/projects/${sourceProjectId}/tasks/${taskId}/move`, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({ target_project_id: targetProjectId }),
      });
      updateTaskInCache(targetProjectId, taskId, moved);
      if (options?.targetProjectTitle) {
        recordRecentMove(targetProjectId, options.targetProjectTitle);
      }
    },
    undoOptimistic: () => {
      setProjectTasks(sourceProjectId, currentTasks);
      removeTaskFromCache(targetProjectId, taskId);
    },
    undo: async () => {
      const restored = await fetchJson<Task>(`/projects/${targetProjectId}/tasks/${taskId}/move`, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({ target_project_id: sourceProjectId }),
      });
      updateTaskInCache(sourceProjectId, taskId, restored);
    },
    commitErrorMessage: 'Failed to move task',
    undoErrorMessage: 'Failed to undo move',
  });
}

/**
 * Toggle task active/pinned state (for "Working On" kanban lane)
 */
export async function toggleTaskActive(
  projectId: string,
  taskId: string,
  currentTasks: Task[],
  isActive?: boolean,
): Promise<TaskMutationResult> {
  if (isAwaitingCreate(taskId)) {
    return whenSaved(projectId, taskId, currentTasks, (id, tasks) =>
      toggleTaskActive(projectId, id, tasks, isActive),
    );
  }
  const task = currentTasks.find((t) => t.id === taskId);
  if (!task) return { success: false, tasks: currentTasks, error: 'Task not found' };

  const newIsActive = isActive ?? !task.isActive;

  return taskMutation<Task>(projectId, currentTasks, {
    applyOptimistic: (tasks) =>
      tasks.map((t) => (t.id === taskId ? { ...t, isActive: newIsActive } : t)),
    apiCall: () =>
      fetchJson<Task>(`/projects/${projectId}/tasks/${taskId}`, {
        method: 'PATCH',
        headers: jsonHeaders(),
        body: JSON.stringify({ task: { is_active: newIsActive } }),
      }),
    onSuccess: (tasks, updated) => tasks.map((t) => (t.id === taskId ? updated : t)),
  });
}

/**
 * Reorder tasks (drag and drop)
 */
export async function reorderTasks(
  projectId: string,
  newTaskIds: string[],
  currentTasks: Task[],
): Promise<TaskMutationResult> {
  if (newTaskIds.some(isAwaitingCreate)) {
    // Wait for every unsaved row in the order, then send real ids only.
    const saved = await Promise.all(
      newTaskIds.map((id) => (isAwaitingCreate(id) ? resolveTaskCreation(id) : undefined)),
    );
    const known = saved.findIndex(Boolean);
    let tasks =
      known >= 0
        ? replayBase(projectId, currentTasks, newTaskIds[known], saved[known])
        : currentTasks;
    const ids: string[] = [];
    newTaskIds.forEach((id, i) => {
      const row = saved[i];
      if (row) {
        tasks = swapTempForSaved(tasks, id, row);
        ids.push(row.id);
      } else if (!isAwaitingCreate(id)) {
        ids.push(id);
      } // else: failed create, no row left to order
    });
    return reorderTasks(projectId, ids, tasks);
  }
  return taskMutation(projectId, currentTasks, {
    applyOptimistic: (tasks) =>
      tasks.map((t) => {
        const newPos = newTaskIds.indexOf(t.id);
        return newPos >= 0 ? { ...t, position: newPos } : t;
      }),
    apiCall: () =>
      fetchVoid(`/projects/${projectId}/tasks/reorder`, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({ task_ids: newTaskIds }),
      }),
  });
}
